import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getPullLocationsDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/locations/pull-locations.form-default-values';
import { PullLocationsModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/locations/pull-locations.model';
import React, { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useEmspBoPullLocationsMutation } from '@app/core/service/emsp-bo-location.service';

import { PullEmspBoLocationContextProvider } from '../emsp-bo-location.context';
import { EmspBoLocationPullCreateFormController } from './emsp-bo-location-pull-create-form/emsp-bo-location-pull-create-form.controller';

interface EmspBoLocationPullCreateControllerProps {
  cpoId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

export const EmspBoLocationPullCreateController = ({
  cpoId,
  onCancel,
  onSuccess,
}: EmspBoLocationPullCreateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [pullLocationsMutation] = useEmspBoPullLocationsMutation();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<PullLocationsModel> = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.emsp-bo-location-pull-create.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<PullLocationsModel> = (
    fieldValues,
  ) => {
    setMultiStepFormState(MultiStepFormState.Loading);

    pullLocationsMutation(fieldValues)
      .unwrap()
      .then(() => {
        toast.success(t('$.emsp-bo-location-pull-create.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.emsp-bo-location-pull-create.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getPullLocationsDefaultValues(cpoId);

  return (
    <PullEmspBoLocationContextProvider data={data}>
      <EmspBoLocationPullCreateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </PullEmspBoLocationContextProvider>
  );
};
